import React, { useState } from "react";
import { formatDateToLocalString } from "components/dashboard/product-template-table/utils/timeUtils";
import { T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { adjustReferenceTime } from "../../utils";

interface TabContextMenuProps {
  tabId: number;
}

const TabContextMenu = ({ tabId }: TabContextMenuProps) => {
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId) || {});
  const setTabContextConfig = useDashboardStore(state => state.setTabContextConfig);
  const triggerTabRefresh = useDashboardStore(state => state.triggerTabRefresh);

  const adjustedTime = adjustReferenceTime(tabContextConfig.config);

  const [from, setFrom] = useState<Date>(
    tabContextConfig.from
      ? new Date(tabContextConfig.from)
      : new Date(adjustedTime.dateFrom || Date.now())
  );
  const [to, setTo] = useState<Date>(
    tabContextConfig.to
      ? new Date(tabContextConfig.to)
      : new Date(adjustedTime.dateTo || Date.now())
  );
  const [referenceTime, setReferenceTime] = useState<Date>(
    tabContextConfig.referenceTime
      ? new Date(tabContextConfig.referenceTime)
      : new Date(adjustedTime.referenceTime || Date.now())
  );

  const updateStore = () => {
    setTabContextConfig(tabId, {
      ...tabContextConfig,
      from: formatDateToLocalString(from),
      to: formatDateToLocalString(to),
      referenceTime: formatDateToLocalString(referenceTime),
    });
    triggerTabRefresh();
  };

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {tabContextConfig?.contextType === ContextTypes.RANGE_DATE_SELECTION && (
        <div className="flex flex-row flex-wrap gap-2">
          <CalendarWrapper
            id="dateFrom"
            value={from}
            onChange={e => setFrom(new Date(e))}
            showTime
            className="align-self-center"
            debounceTimeInMs={100}
            tooltip={T("word.date-from")}
            tooltipOptions={{ position: "bottom" }}
          />
          <CalendarWrapper
            id="dateTo"
            value={to}
            onChange={e => setTo(new Date(e))}
            showTime
            className="align-self-center"
            debounceTimeInMs={100}
            tooltip={T("word.date-to")}
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
      )}
      {(tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION_WITH_CONFIG ||
        tabContextConfig?.contextType === ContextTypes.SINGLE_DATE_SELECTION) && (
        <CalendarWrapper
          id="referenceTime"
          value={referenceTime}
          onChange={e => setReferenceTime(new Date(e))}
          className="align-self-center"
          debounceTimeInMs={100}
          tooltip={T("load-conf.reference-time")}
          tooltipOptions={{ position: "bottom" }}
        />
      )}
      {tabContextConfig?.contextType === ContextTypes.YEARS_SELECTION && (
        <CalendarWrapper
          id="years"
          view="year"
          value={from}
          onChange={e => {
            const selectedYear = e.getFullYear();
            setFrom(new Date(selectedYear, 0, 1));
            const newTo = new Date(selectedYear + 1, 0, 1);
            newTo.setMinutes(newTo.getMinutes() - 1);
            setTo(newTo);
          }}
          dateFormat="yy"
          className="align-self-center"
          debounceTimeInMs={100}
          showTime={false}
          tooltip={T("time.years")}
          tooltipOptions={{ position: "bottom" }}
        />
      )}
      <Button
        icon="pi pi-sync"
        tooltip={T("action.fetch-data")}
        tooltipOptions={{ position: "bottom" }}
        onClick={updateStore}
        className="p-button-info align-self-center"
      />
    </div>
  );
};

export default TabContextMenu;
